import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {components} from 'react-select';
import {HiArrowLeft, HiCheck, HiX} from 'react-icons/hi';
import {Spinner, Tooltip} from 'flowbite-react';
import {MultiSelect, SingleSelect} from "../../../components/common/Search";
import { ENTITY } from '../../../redux/slices/timeline';

// Custom React Tooltip Component
interface CustomTooltipProps {
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ content, children, className = "" }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setVisible(true);
  };

  const handleMouseLeave = () => {
    setVisible(false);
  };

  return (
    <div 
      ref={containerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {visible && (
        <div
          ref={tooltipRef}
          className="fixed z-[999999] px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full"
          style={{
            left: position.x,
            top: position.y,
            maxWidth: '300px',
            wordWrap: 'break-word'
          }}
        >
          {content}
          <div 
            className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"
          />
        </div>
      )}
    </div>
  );
};

interface ProjectOption {
  value: string;
  label: string;
  project_start_date?: string;
  project_end_date?: string;
  task_start_date?: string;
  task_end_date?: string;
  adj_start_date?: string;
  adj_end_date?: string;
  duration?: number;
  isDisabled?: boolean;
  error?: string;
}

interface DependencyFormProps {
  isEditing: boolean;
  isLoading: boolean;
  selectedProject: string;
  dependsOn: string[];
  projectOptions: ProjectOption[];
  dependencyOptions: ProjectOption[];
  onProjectChange: (value: string) => void;
  onDependsOnChange: (values: string[]) => void;
  onSave: () => void;
  onCancel: () => void;
  childEntityType?: string; // New prop for dynamic entity type
  disableDateValidation?: boolean;
}

const DependencyForm: React.FC<DependencyFormProps> = ({
  isEditing,
  isLoading,
  selectedProject,
  dependsOn,
  projectOptions,
  dependencyOptions,
  onProjectChange,
  onDependsOnChange,
  onSave,
  onCancel,
  childEntityType,
  disableDateValidation
}) => {
  const { currentEntity } = useSelector((state: any) => state.timeline || {});
  const isTaskLevel = currentEntity === ENTITY.PROJECT;
  // Function to validate dependency conditions based on backend logic
  // Backend conditions from check_dependency_conditions:
  // 1. Both projects must be in the roadmap (handled by option availability)
  // 2. The dependent project cannot start before the project it depends on
  // 3. The dependent project cannot end before the project it depends on
  const validateDependencyCondition = (dependentProject: ProjectOption, dependencyProject: ProjectOption): { isValid: boolean; error?: string } => {
    if (!dependentProject || !dependencyProject) {
      return { isValid: false, error: "Project data not available" };
    }

    const dependentStartDate = isTaskLevel
      ? (dependentProject.task_start_date || dependentProject.project_start_date || dependentProject.adj_start_date)
      : dependentProject.project_start_date;
    const dependentEndDate = isTaskLevel
      ? (dependentProject.task_end_date || dependentProject.project_end_date || dependentProject.adj_end_date)
      : dependentProject.project_end_date;
    const dependencyStartDate = isTaskLevel
      ? (dependencyProject.task_start_date || dependencyProject.project_start_date || dependencyProject.adj_start_date)
      : dependencyProject.project_start_date;
    const dependencyEndDate = isTaskLevel
      ? (dependencyProject.task_end_date || dependencyProject.project_end_date || dependencyProject.adj_end_date)
      : dependencyProject.project_end_date;

    if (!dependentStartDate || !dependentEndDate || !dependencyStartDate || !dependencyEndDate) {
      return { isValid: false, error: "Missing date information" };
    }

    const dependentStart = new Date(dependentStartDate);
    const dependentEnd = new Date(dependentEndDate);
    const dependencyStart = new Date(dependencyStartDate);
    const dependencyEnd = new Date(dependencyEndDate);

    // Backend condition 2: The dependent project cannot start before the project it depends on
  if (dependencyStart > dependentStart) {
    return {
      isValid: false,
      error: `Cannot depend on this project. The dependent project must start on or after the project it depends on.`,
    };
  }

    // Backend condition 3: The dependent project cannot end before the project it depends on
    if (dependencyEnd > dependentEnd) {
      return {
        isValid: false,
        error: `Cannot depend on this project. The dependent project must end on or after the project it depends on.`,
      };
    }

    return { isValid: true };
  };

  // Process dependency options to add validation
  const processedDependencyOptions = React.useMemo(() => {
    if (!selectedProject) return dependencyOptions;

    const selectedProjectData = projectOptions.find(p => p.value === selectedProject);
    if (!selectedProjectData) return dependencyOptions;

    return dependencyOptions.map(option => {
      // Don't allow a project to depend on itself
      if (option.value === selectedProject) {
        return {
          ...option,
          isDisabled: true,
          error: "A project cannot depend on itself"
        };
      }

      if (disableDateValidation) {
        return option;
      }

      const validation = validateDependencyCondition(selectedProjectData, option);
      
      if (!validation.isValid) {
        return {
          ...option,
          isDisabled: true,
          error: validation.error
        };
      }

      return option;
    });
  }, [selectedProject, projectOptions, dependencyOptions]);

  // Check if there are any validation errors in current selection
  const hasValidationErrors = React.useMemo(() => {
    if (!selectedProject || dependsOn.length === 0) return false;
    
    return dependsOn.some(depId => {
      const option = processedDependencyOptions.find(opt => opt.value === depId);
      return option?.isDisabled;
    });
  }, [selectedProject, dependsOn, processedDependencyOptions]);

  // Filter out disabled options from the onChange handler
  const handleDependencyChange = (values: any[]) => {
    const validValues = values.filter(v => {
      const option = processedDependencyOptions.find(opt => opt.value === v.value);
      return !option?.isDisabled;
    });
    onDependsOnChange(validValues.map((v: any) => v.value));
  };

  // Convert plural childEntityType to singular using major English pluralization rules
  const getSingularEntityType = (entityType: string) => {
    if (!entityType) return "Project";

    const lower = entityType.toLowerCase();

    // Special case: Tasks should become User Story
    if (lower === 'tasks') {
      return "User Story";
    }

    // Words ending in -ies (e.g., Stories -> Story, Companies -> Company)
    if (lower.endsWith('ies')) {
      return entityType.slice(0, -3) + 'y';
    }

    // Words ending in -es after s, x, z, ch, sh (e.g., Boxes -> Box, Dishes -> Dish)
    if (lower.endsWith('es') && (lower.endsWith('ses') || lower.endsWith('xes') || lower.endsWith('zes') || lower.endsWith('ches') || lower.endsWith('shes'))) {
      return entityType.slice(0, -2);
    }

    // Words ending in -ves (e.g., Lives -> Life, Knives -> Knife)
    if (lower.endsWith('ves')) {
      return entityType.slice(0, -3) + 'fe';
    }

    // Words ending in -s but not -ss (e.g., Projects -> Project, but not Glass -> Glas)
    if (lower.endsWith('s') && !lower.endsWith('ss')) {
      return entityType.slice(0, -1);
    }

    // Return as-is if no plural pattern matches
    return entityType;
  };

  const singularEntityType = getSingularEntityType(childEntityType || "Projects");
  return (
    <div
      className="w-full p-4 rounded-xl shadow-xl bg-light-surfacePrimary dark:bg-secondary-dark border border-light-border-default dark:border-slate-700  mx-auto mt-2">
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-8">
          <Spinner className="text-primary-light"/>
          <span className="mt-2 text-sm text-light-text-secondary dark:text-gray-300">
            {isEditing ? 'Saving changes...' : 'Saving dependency...'}
          </span>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={onCancel}
              className="p-1.5 rounded-full hover:bg-light-interactive-hover dark:hover:bg-slate-700 text-light-text-secondary hover:text-light-text-primary transition-colors duration-200"
            >
              {isEditing ? <HiX size={16}/> : <HiArrowLeft size={16}/>}
            </button>
            <h2 className="text-base font-bold text-light-text-primary dark:text-white">
              {isEditing ? 'Edit Dependencies' : 'Add Dependency'}
            </h2>
          </div>

          <div className="mb-3">
            <label
              className="block text-base font-semibold mb-2 text-light-text-primary dark:text-gray-200">{singularEntityType}</label>
            <SingleSelect
              name="select-project"
              options={projectOptions}
              placeholder="Select..."
              onChange={(value: any) => onProjectChange(value?.value)}
              defaultValue={projectOptions.find(p => p.value === selectedProject)}
              width="w-full"
            />
          </div>

          <div className="my-3 border-t border-light-border-default dark:border-slate-700"/>

          <div className="mb-3">
            <label className="block text-base font-semibold mb-2 text-light-text-primary dark:text-gray-200">
              Depends On
            </label>
            <MultiSelect
              name="select-project-dependency"
              options={processedDependencyOptions}
              value={processedDependencyOptions.filter(p => dependsOn.includes(p.value) && !p.isDisabled && !p.isInvalid)}
              onChange={handleDependencyChange}
              placeholder="Select..."
              isClearable={false}
              width="w-full"
              components={{
                Option: (props: any) => {
                  const {data, innerRef, innerProps, isDisabled} = props;

                  const formatDate = (dateStr: string) => {
                    if (!dateStr) return 'N/A';
                    return new Date(dateStr).toLocaleDateString();
                  };

                  // Helper to get the actual start/end dates for display
                  const getDisplayDates = (projectData: ProjectOption) => {
                    const startDate = projectData.task_start_date || projectData.project_start_date || projectData.adj_start_date;
                    const endDate = projectData.task_end_date || projectData.project_end_date || projectData.adj_end_date;
                    return { startDate, endDate };
                  };

                  const displayDates = getDisplayDates(data);

                  if (data.isDisabled) {
                    return (
                      <CustomTooltip
                        content={
                          <div className="text-xs">
                            <div className="font-semibold mb-2 text-red-200">{data.error}</div>
                            {displayDates.startDate && (
                              <div className="space-y-1 text-gray-300">
                                <div>
                                  <span className="font-medium">Start:</span> {formatDate(displayDates.startDate)}
                                </div>
                                {displayDates.endDate && (
                                  <div>
                                    <span className="font-medium">End:</span> {formatDate(displayDates.endDate)}
                                  </div>
                                )}
                                {data.duration && (
                                  <div>
                                    <span className="font-medium">Duration:</span> {data.duration} day{data.duration !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        }
                        className="w-full"
                      >
                        <div
                          ref={innerRef}
                          {...innerProps}
                          className="cursor-not-allowed px-3 py-2 flex items-center justify-between bg-gray-50 dark:bg-gray-800"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center gap-2">
                            <span className="opacity-50 text-gray-500 dark:text-gray-400 text-sm font-medium">{data.label}</span>
                            <span className="text-xs text-red-400 font-medium">⚠</span>
                          </div>
                        </div>
                      </CustomTooltip>
                    );
                  }

                  return <components.Option {...props} />;
                }
              }}
            />
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <button
              className="px-3 py-1.5 text-xs rounded-lg bg-light-surfaceSecondary text-light-text-primary font-semibold hover:bg-light-interactive-hover dark:bg-slate-700 dark:text-gray-200 dark:hover:bg-slate-600 flex items-center gap-1 border border-light-border-default dark:border-slate-600 transition-colors duration-200"
              onClick={onCancel}
            >
              <HiX size={14}/> Cancel
            </button>
            {(() => {
              const isDisabled = !selectedProject || dependsOn.length === 0 || hasValidationErrors;
              const tooltipMessage = hasValidationErrors
                ? "Cannot save: Some selected dependencies violate the dependency conditions"
                : "";
              const saveButton = (
                <button
                  className={`px-3 py-1.5 rounded-lg font-semibold text-xs flex items-center gap-1 transition-colors duration-200 ${
                    isDisabled
                      ? 'bg-light-surfaceSecondary text-light-text-muted cursor-not-allowed border border-light-border-default'
                      : 'bg-primary-light text-white hover:bg-primary-hover shadow-sm'
                  }`}
                  onClick={onSave}
                  disabled={isDisabled}
                >
                  <HiCheck size={14}/> {isEditing ? ' Update Changes' : 'Add Dependency'}
                </button>
              );
              // Only show tooltip when there are validation errors to avoid blocking the dropdown
              if (tooltipMessage) {
                return (
                  <Tooltip content={tooltipMessage} style="dark">
                    {saveButton}
                  </Tooltip>
                );
              }
              return saveButton;
            })()}
          </div>
        </>
      )}
    </div>
  );
};

export default DependencyForm; 